import { z } from "zod";
import { AiPolicyDecisionSchema, EffectiveAiPolicySchema } from "./ai-policy";

const uuidSchema = z.string().uuid();
const moneySchema = z.number().finite().nonnegative();

export const BidProposalActionTypeSchema = z.enum(["increase_bid", "decrease_bid"]);
export type BidProposalActionType = z.infer<typeof BidProposalActionTypeSchema>;

export const TargetConclusionTypeSchema = z.enum([
  "keep_bid",
  "observe",
  "insufficient_data",
  "unsupported",
]);
export type TargetConclusionType = z.infer<typeof TargetConclusionTypeSchema>;

export const ProposalSupportingMetricsSchema = z
  .object({
    acos: z.number().nonnegative().optional(),
    targetAcos: z.number().nonnegative().optional(),
    clicks: z.number().int().nonnegative().optional(),
    impressions: z.number().int().nonnegative().optional(),
    spend: z.number().nonnegative().optional(),
    sales: z.number().nonnegative().optional(),
    orders: z.number().int().nonnegative().optional(),
    cvr: z.number().nonnegative().optional(),
    ctr: z.number().nonnegative().optional(),
    cpc: z.number().nonnegative().optional(),
    window: z.string().min(1).optional(),
  })
  .strict();

export const ProposalAnalysisSchema = z
  .object({
    summary: z.string().min(5).max(200),
    keyFindings: z.array(z.string().max(180)).max(10),
    campaignHealth: z.enum(["healthy", "watch", "at_risk"]),
  })
  .strict();

export const ProposalTargetSnapshotSchema = z
  .object({
    id: uuidSchema,
    adGroupId: uuidSchema.nullable(),
    targetType: z.enum(["keyword", "auto", "product", "product_category", "theme"]),
    label: z.string().min(1),
    expressionType: z.enum(["close_match", "loose_match", "substitutes", "complements"]).nullable(),
    status: z.enum(["enabled", "paused", "archived"]),
    currentBid: moneySchema.nullable(),
  })
  .strict();

export const ProposalTrustedMetricWindowSchema = z
  .object({
    windowStart: z.string().min(1),
    windowEnd: z.string().min(1),
    dataThrough: z.string().min(1).nullable(),
    coverageComplete: z.boolean(),
    clicks: z.number().int().nonnegative(),
    spend: z.number().nonnegative(),
    sales: z.number().nonnegative(),
    orders: z.number().int().nonnegative(),
    acos: z.number().nonnegative(),
  })
  .strict();

const proposalSearchTermEvidenceSchema = z
  .object({
    resolvedRows: z.number().int().nonnegative(),
    metrics: z
      .object({
        impressions: z.number().nonnegative(),
        clicks: z.number().nonnegative(),
        spend: z.number().nonnegative(),
        sales: z.number().nonnegative(),
        orders: z.number().nonnegative(),
      })
      .strict(),
    candidates: z.array(
      z
        .object({
          text: z.string(),
          range: z.enum(["7d", "14d", "30d"]),
          clicks: z.number().nonnegative(),
          spend: z.number().nonnegative(),
          orders: z.number().nonnegative(),
          negativeKeywordCandidate: z.boolean(),
          reason: z.literal("spend_without_orders").nullable(),
        })
        .strict(),
    ),
  })
  .strict();

const proposalRiskWarningSchema = z
  .object({
    code: z.enum([
      "single_change_medium",
      "single_change_high",
      "single_change_critical",
      "weekly_change_critical",
      "adpilot_bid_range",
    ]),
    level: z.enum(["medium", "high", "critical"]),
    threshold: z.union([z.number(), z.object({ min: z.number(), max: z.number() }).strict()]),
    actualValue: z.number(),
  })
  .strict();

export const ProposalArtifactActionSchema = z
  .object({
    id: uuidSchema,
    target: ProposalTargetSnapshotSchema,
    agent: z
      .object({
        actionType: BidProposalActionTypeSchema,
        claimedCurrentBid: moneySchema.optional(),
        proposedBid: moneySchema,
        confidence: z.enum(["low", "medium", "high"]),
        reasoning: z.string().min(10).max(400),
        supportingMetrics: ProposalSupportingMetricsSchema,
      })
      .strict(),
    server: z
      .object({
        oldBid: moneySchema,
        newBid: moneySchema,
        changePercentage: z.number().finite(),
        weeklyUsage: z.number().finite().nonnegative(),
        riskLevel: z.enum(["low", "medium", "high", "critical"]),
        route: z.enum(["approval", "auto", "recommendation"]),
        guardrailPassed: z.literal(true),
        reasonCodes: z.array(z.string().min(1)),
        warnings: z.array(proposalRiskWarningSchema),
        policyDecision: AiPolicyDecisionSchema,
        trustedMetrics: ProposalTrustedMetricWindowSchema.nullable(),
        searchTermEvidence: proposalSearchTermEvidenceSchema.nullable(),
      })
      .strict(),
  })
  .strict();

export const ProposalArtifactConclusionSchema = z
  .object({
    id: uuidSchema,
    target: ProposalTargetSnapshotSchema,
    agent: z
      .object({
        conclusion: TargetConclusionTypeSchema,
        confidence: z.enum(["low", "medium", "high"]),
        reasoning: z.string().min(10).max(400),
        supportingMetrics: ProposalSupportingMetricsSchema,
      })
      .strict(),
    server: z
      .object({
        trustedMetrics: ProposalTrustedMetricWindowSchema.nullable(),
        searchTermEvidence: proposalSearchTermEvidenceSchema.nullable(),
      })
      .strict(),
  })
  .strict();

export const AiProposalArtifactV1Schema = z
  .object({
    schemaVersion: z.literal(1),
    analysis: ProposalAnalysisSchema,
    actions: z.array(ProposalArtifactActionSchema),
    conclusions: z.array(ProposalArtifactConclusionSchema),
    validation: z
      .object({
        policySnapshotId: uuidSchema,
        roundNumber: z.number().int().positive(),
        candidateAttempt: z.number().int().min(1).max(3),
        validatedAt: z.string().datetime({ offset: true }),
        proposalRisk: z.enum(["low", "medium", "high", "critical"]).nullable(),
        effectivePolicy: EffectiveAiPolicySchema,
      })
      .strict(),
  })
  .strict();
export type AiProposalArtifactV1 = z.infer<typeof AiProposalArtifactV1Schema>;
